import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon, Save, Globe, Store, Home,
  CreditCard, Mail, Share2, Shield, RefreshCw,
  Upload, Eye, EyeOff, AlertCircle, CheckCircle,
  Loader2, XCircle, Image as ImageIcon
} from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  
  const API_BASE = 'https://api.maechi.in';
  
  // Categories with icons
  const categories = [
    { id: 'general', label: 'General', icon: <Globe size={20} /> },
    { id: 'store', label: 'Store', icon: <Store size={20} /> },
    { id: 'homepage', label: 'Homepage', icon: <Home size={20} /> },
    { id: 'payment', label: 'Payment', icon: <CreditCard size={20} /> },
    { id: 'social', label: 'Social Media', icon: <Share2 size={20} /> },
    { id: 'email', label: 'Email', icon: <Mail size={20} /> },
    { id: 'advanced', label: 'Advanced', icon: <Shield size={20} /> }
  ];
  
  // Fetch settings
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/settings`);
      const data = await response.json();
      
      if (data.success) {
        // Convert array to object for easier access
        const settingsObj = {};
        data.data.forEach(setting => {
          settingsObj[setting.key] = setting;
        });
        setSettings(settingsObj);
      } else {
        setError(data.message || 'Failed to load settings');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle input change
  const handleInputChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: value
      }
    }));
  };
  
  // Handle file upload (for images)
  const handleFileUpload = async (key, file) => {
    // In real implementation, upload to server and get URL
    // For now, using base64
    const reader = new FileReader();
    reader.onloadend = () => {
      handleInputChange(key, reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  // Save settings
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Prepare data for API
      const updateData = {};
      Object.keys(settings).forEach(key => {
        updateData[key] = settings[key].value;
      });
      
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to save settings');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Save Error:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to defaults
  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default?')) {
      fetchSettings();
    }
  };
  
  // Get settings by active tab
  const getCurrentSettings = () => {
    return Object.values(settings).filter(s => s.category === activeTab);
  };
  
  // Render input field based on type
  const renderInput = (setting) => {
    const { key, value, type, label, description, options } = setting;
    
    switch (type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );
        
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleInputChange(key, e.target.value)}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );
        
      case 'number':
        return (
          <input
            type="number"
            value={value || 0}
            onChange={(e) => handleInputChange(key, parseFloat(e.target.value))}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        );
        
      case 'boolean':
        return (
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => handleInputChange(key, !value)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                value ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                value ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
            <span className="ml-3 text-sm text-gray-600">
              {value ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        );
        
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {options && options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
        
      case 'json':
        return (
          <div className="space-y-2">
            {Array.isArray(value) && value.map((item, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => {
                    const newValue = [...value];
                    newValue[index] = e.target.value;
                    handleInputChange(key, newValue);
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                />
                <button
                  onClick={() => {
                    const newValue = value.filter((_, i) => i !== index);
                    handleInputChange(key, newValue);
                  }}
                  className="px-3 py-2 text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                const newValue = [...(value || []), ''];
                handleInputChange(key, newValue);
              }}
              className="px-4 py-2 text-blue-600 hover:text-blue-800"
            >
              Add Item
            </button>
          </div>
        );
        
      case 'image':
        return (
          <div className="space-y-3">
            {value && (
              <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-gray-300">
                <img
                  src={value}
                  alt={label}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div>
              <input
                type="file"
                id={`file-${key}`}
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleFileUpload(key, e.target.files[0]);
                  }
                }}
                className="hidden"
              />
              <label
                htmlFor={`file-${key}`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <Upload size={16} className="mr-2" />
                {value ? 'Change Image' : 'Upload Image'}
              </label>
              {value && (
                <button
                  onClick={() => handleInputChange(key, '')}
                  className="ml-3 px-4 py-2 text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        );
        
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
          />
        );
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center shadow-lg max-w-md">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
          <span className="text-green-800">{successMessage}</span>
          <button onClick={() => setSuccessMessage('')} className="ml-auto">
            <XCircle className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}
      
      {error && (
        <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center shadow-lg max-w-md">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <XCircle className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <SettingsIcon className="mr-3" size={24} />
                Settings
              </h1>
              <p className="text-gray-600 mt-1">Manage your store configuration</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleReset}
                className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
              >
                <RefreshCw size={18} className="mr-2" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-4">Settings Categories</h3>
              <nav className="space-y-1">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveTab(category.id)}
                    className={`w-full flex items-center px-4 py-3 rounded-lg text-left ${
                      activeTab === category.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="mr-3">{category.icon}</span>
                    <span>{category.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
          
          {/* Right Content - Settings Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-gray-200">
              {/* Category Header */}
              <div className="border-b border-gray-200 p-6">
                <div className="flex items-center">
                  {categories.find(c => c.id === activeTab)?.icon}
                  <h2 className="text-xl font-bold text-gray-900 ml-3">
                    {categories.find(c => c.id === activeTab)?.label} Settings
                  </h2>
                </div>
                <p className="text-gray-600 mt-2">
                  Configure {activeTab} settings for your store
                </p>
              </div>
              
              {/* Settings Form */}
              <div className="p-6">
                {getCurrentSettings().length === 0 ? (
                  <div className="text-center py-12">
                    <SettingsIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No settings found for this category</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {getCurrentSettings().map(setting => (
                      <div key={setting.key} className="pb-6 border-b border-gray-100 last:border-0">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-900 mb-1">
                              {setting.label}
                            </label>
                            {setting.description && (
                              <p className="text-sm text-gray-500">{setting.description}</p>
                            )}
                          </div>
                          <div className="md:col-span-2">
                            {renderInput(setting)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Save Button for Mobile */}
              <div className="border-t border-gray-200 p-6 lg:hidden">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Saving Changes...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Additional Info Based on Category */}
            {activeTab === 'payment' && (
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-900 mb-2">Razorpay Configuration</h4>
                    <p className="text-yellow-800 text-sm mb-3">
                      To get your Razorpay API keys:
                    </p>
                    <ol className="text-yellow-800 text-sm space-y-2 list-decimal list-inside">
                      <li>Login to your Razorpay Dashboard</li>
                      <li>Go to Settings → API Keys</li>
                      <li>Generate new API keys or use existing ones</li>
                      <li>For test mode, use test keys. For live mode, use live keys</li>
                      <li>Make sure to whitelist your domain in Razorpay settings</li>
                    </ol>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'email' && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-2">Email Configuration</h4>
                    <p className="text-blue-800 text-sm">
                      Configure SMTP settings to enable email notifications for orders, 
                      password resets, and customer communications.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'homepage' && (
              <div className="mt-6 bg-purple-50 border border-purple-200 rounded-xl p-6">
                <div className="flex items-start">
                  <ImageIcon className="w-5 h-5 text-purple-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-purple-900 mb-2">Banner Recommendations</h4>
                    <ul className="text-purple-800 text-sm space-y-2">
                      <li>• Recommended banner image size: 1920x600 pixels</li>
                      <li>• Use high-quality images for better appearance</li>
                      <li>• Keep banner title short and catchy</li>
                      <li>• Description should be clear and compelling</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;